import React, { useEffect, useState, useRef } from "react";
import Slider from "react-slick";
import { Box, Typography } from "@mui/material";
import useAuth from "../../hooks/Auth/useAuth";
import axios from "axios";
import Swal from "sweetalert2";
import { CircularProgressbar, buildStyles } from "react-circular-progressbar";
import "react-circular-progressbar/dist/styles.css";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";

const camerasList = [
  { camera_name: "left", show: true },
  { camera_name: "front", show: true },
  { camera_name: "right", show: true },
  { camera_name: "incabin", show: true },
];

const SOCKET_TIMEOUT = 6000;        // 6 s
const SPIN_PERIOD    = 1200;        // ms for one full revolution

export default function CartCamerasCarousel({ cartId }) {
  const { relevantUrl, relevantSite, sitesSockectsConnection, auth } = useAuth();

  const [cameraImages, setCameraImages] = useState([]);
  const [loading, setLoading]       = useState(true);
  const [spinner, setSpinner]       = useState(0);     // 0‑100

  const timeoutRef = useRef(null);
  const rafRef     = useRef(null);

  /* ───────────────────────── spinner animation ─────────────────────── */
  useEffect(() => {
    if (!loading) {
      cancelAnimationFrame(rafRef.current);
      setSpinner(0);
      return;
    }
    const start = performance.now();
    const animate = (t) => {
      const pct = ((t - start) % SPIN_PERIOD) / SPIN_PERIOD * 100;
      setSpinner(pct);
      rafRef.current = requestAnimationFrame(animate);
    };
    rafRef.current = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(rafRef.current);
  }, [loading]);

  /* ───────────────────────── socket handler ────────────────────────── */
  const handleSocketData = (data) => {
    const { cameras_data_list } = JSON.parse(data);
    const imgs = (cameras_data_list || [])
      .filter((c) => c.image_base64)
      .map((c) => ({
        name: c.camera_name,
        image: `data:image/jpeg;base64,${c.image_base64}`,
      }));

    clearTimeout(timeoutRef.current);
    setCameraImages(imgs);
    setLoading(false);
  };

  /* ───────────────────────── subscribe / unsubscribe ───────────────── */
  useEffect(() => {
    if (!relevantSite || !sitesSockectsConnection?.[relevantSite]) return;
    const sock = sitesSockectsConnection[relevantSite];

    sock.on("cartCamerasData", handleSocketData);
    timeoutRef.current = setTimeout(() => setLoading(false), SOCKET_TIMEOUT);

    return () => {
      clearTimeout(timeoutRef.current);
      sock.emit("sm_turn_off_cameras_event", cartId);
      sock.off("cartCamerasData", handleSocketData);
    };
  }, [sitesSockectsConnection, relevantSite, cartId]);

  /* ───────────────────────── request cameras once ──────────────────── */
  useEffect(() => {
    if (!relevantUrl) return;

    const requestCameras = async () => {
      try {
        const camera_commands_list = camerasList.map(({ camera_name, show }) => ({
          camera_name,
          show,
        }));

        const r = await axios.post(`${relevantUrl}/admin/requestCameras`, {
          camera_commands_list,
          cartId,
          adminId: auth?.userId || null,
        });

        if (r.status !== 200) {
          Swal.fire({ icon: "error", title: "Failed to request cameras", text: r.data });
          setLoading(false);
        }
      } catch (err) {
        Swal.fire({ icon: "error", title: "Error", text: err.message });
        setLoading(false);
      }
    };

    setCameraImages([]);
    setLoading(true);
    requestCameras();
  }, [relevantUrl, cartId, auth]);

  /* ───────────────────────── slick settings ────────────────────────── */
  const settings = {
    dots: true,
    infinite: true,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    arrows: true,
  };

  /* ───────────────────────── render ────────────────────────────────── */
  return (
    <Box
      sx={{
        width: "90%",
        maxWidth: 600,
        p: 2,
        mb: 2,
        bgcolor: "#f5f5f5",
        borderRadius: 2,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        minHeight: 260,
      }}
    >
      {loading ? (
        <Box sx={{ width: 90, height: 90 }}>
          <CircularProgressbar
            value={spinner}
            strokeWidth={6}
            styles={buildStyles({
              pathColor: "#1976d2",
              trailColor: "#e0e0e0",
              pathTransitionDuration: 0, // immediate jumps → smooth rotation
              strokeLinecap: "butt",
            })}
          />
        </Box>
      ) : cameraImages.length ? (
        <Slider {...settings} style={{ width: "100%" }}>
          {cameraImages.map(({ name, image }) => (
            <div key={name} style={{ textAlign: "center" }}>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                {name[0].toUpperCase() + name.slice(1)}
              </Typography>
              <img
                src={image}
                alt={name}
                style={{ width: "100%", maxHeight: 400, objectFit: "contain" }}
              />
            </div>
          ))}
        </Slider>
      ) : (
        <Typography variant="body2" sx={{ color: "text.secondary" }}>
          No camera images available
        </Typography>
      )}
    </Box>
  );
}
